import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosPost from '../helpers/axios-helper';
import '../styles/Blog.css';

export default function CountryProfile() {
    const { countryId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [country, setCountry] = useState(null);
    const [profile, setProfile] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const closeLightbox = () => setLightboxOpen(false);
    const showPrev = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
    const showNext = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

    // Keyboard navigation for lightbox
    useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') showPrev();
            if (e.key === 'ArrowRight') showNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxOpen, lightboxImages.length]);

    useEffect(() => {
        if (!countryId) {
            navigate('/');
            return;
        }

        // Load country data first
        axiosPost(
            'countries/get',
            { 'Content-Type': 'application/json' },
            { id: parseInt(countryId) },
            (resp) => {
                if (resp?.success && resp?.data && resp.data.length > 0) {
                    setCountry(resp.data[0]);
                    
                    // Then try to load the editable profile
                    axiosPost(
                        'country-profiles/get',
                        { 'Content-Type': 'application/json' },
                        { country_id: parseInt(countryId) },
                        (profileResp) => {
                            if (profileResp?.success && profileResp?.data) {
                                setProfile(profileResp.data);
                            }
                            setLoading(false);
                        },
                        (err) => {
                            console.error('Failed to load profile:', err);
                            setLoading(false);
                        }
                    );
                } else {
                    setCountry(null);
                    setLoading(false);
                }
            },
            (err) => {
                console.error('Failed to load country:', err);
                setCountry(null);
                setLoading(false);
            }
        );
    }, [countryId, navigate]);

    // Render blocks (same as Blog.js)
    const renderBlocks = (blocks) => {
        if (!Array.isArray(blocks)) return null;
        return blocks.map((b, idx) => {
            if (b.type === 'header') {
                return (
                    <h2 key={idx} className="blog-block-header">
                        {b.content}
                    </h2>
                );
            }
            if (b.type === 'image') {
                const c = b.content;
                if (typeof c === 'string') {
                    return (
                        <div key={idx} className="blog-image-item">
                            <img
                                src={c}
                                alt=""
                                className="blog-block-image blog-image-clickable"
                                onClick={() => {
                                    setLightboxImages([c]);
                                    setLightboxIndex(0);
                                    setLightboxOpen(true);
                                }}
                            />
                        </div>
                    );
                }
                if (c && Array.isArray(c.images)) {
                    const images = c.images;
                    return (
                        <div key={idx} className={images.length > 1 ? 'blog-images-grid' : ''}>
                            {images.map((im, i) => (
                                <div key={`img-${idx}-${i}`} className="blog-image-item">
                                    <img
                                        src={im.url}
                                        alt={im.caption || ''}
                                        className="blog-block-image blog-image-clickable"
                                        onClick={() => {
                                            const urls = images.map(m => m.url);
                                            setLightboxImages(urls);
                                            setLightboxIndex(i);
                                            setLightboxOpen(true);
                                        }}
                                    />
                                    {im.caption && (
                                        <div className="blog-image-caption">{im.caption}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'video') {
                const c = b.content;
                if (c && c.url) {
                    const formatVideoUrl = (url) => {
                        if (url.includes('youtube.com/watch')) {
                            const videoId = new URL(url).searchParams.get('v');
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('youtu.be')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('vimeo.com')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://player.vimeo.com/video/${videoId}`;
                        }
                        return url;
                    };
                    return (
                        <div key={idx} className="blog-video-container">
                            <iframe
                                src={formatVideoUrl(c.url)}
                                title={c.caption || 'Video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                            {c.caption && (
                                <div className="blog-image-caption">{c.caption}</div>
                            )}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'text') {
                const c = b.content;
                if (typeof c === 'string') {
                    if (c.startsWith('<') || c.includes('<p>') || c.includes('<h')) {
                        return (
                            <div 
                                key={idx} 
                                className="blog-block-html"
                                dangerouslySetInnerHTML={{ __html: c }}
                            />
                        );
                    }
                    return (
                        <p key={idx} className="blog-block-paragraph">
                            {c}
                        </p>
                    );
                }
            }
            return null;
        });
    };

    // Fallback: Convert plain text to paragraphs
    const formatContent = (text) => {
        if (!text) return null;
        const paragraphs = text.split(/\n\s*\n|\n/).filter(p => p.trim());
        return paragraphs.map((paragraph, idx) => {
            const trimmed = paragraph.trim();
            if (/^\d+\.\s+[A-Z]/.test(trimmed)) {
                return (
                    <h2 key={idx} className="blog-block-h2" style={{ marginTop: idx > 0 ? '40px' : '20px' }}>
                        {trimmed}
                    </h2>
                );
            }
            if (trimmed.length < 100 && (trimmed.endsWith(':') || /^[A-Z\s&]+$/.test(trimmed))) {
                return (
                    <h3 key={idx} className="blog-block-h3" style={{ marginTop: '25px' }}>
                        {trimmed}
                    </h3>
                );
            }
            return (
                <p key={idx} className="blog-block-paragraph">
                    {trimmed}
                </p>
            );
        });
    };

    if (loading) return <div className="blog-loading">Loading...</div>;
    if (!country) return <div className="blog-not-found">Country not found.</div>;

    // Use profile data if available, otherwise fallback to country data
    const hasProfile = profile && profile.blocks && profile.blocks.length > 0;
    const heroImage = (hasProfile && profile.featured_image_url) ? profile.featured_image_url : country.image;
    const title = (hasProfile && profile.title) ? profile.title : `${country.name} Travel Guide`;

    return (
        <div className="blog-container" style={{ paddingTop: '110px' }}>
            {/* Hero Image */}
            {heroImage && (
                <img
                    src={heroImage}
                    alt={country.name}
                    className="blog-hero"
                />
            )}

            {/* Title */}
            <h1 className="blog-title">{title}</h1>

            {/* Meta */}
            <div className="blog-meta">
                <span className="blog-category">Country Profile</span>
                <span className="blog-meta-sep">|</span>
                <span>{country.continent || 'Caribbean'}</span>
            </div>

            {/* Excerpt */}
            {hasProfile && profile.excerpt && (
                <div className="blog-excerpt">{profile.excerpt}</div>
            )}

            {/* Content */}
            <div className="blog-blocks">
                {hasProfile ? renderBlocks(profile.blocks) : formatContent(country.info)}
            </div>

            {/* Back button */}
            <div style={{ padding: '40px 175px' }}>
                <button 
                    onClick={() => navigate(-1)}
                    style={{
                        padding: '12px 28px',
                        background: '#2c2c2c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontFamily: 'Lato, sans-serif'
                    }}
                >
                    ← Back to {country.name}
                </button>
            </div>

            {/* Lightbox Overlay */}
            {lightboxOpen && lightboxImages.length > 0 && (
                <div className="lightbox-backdrop" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
                        {lightboxImages.length > 1 && (
                            <>
                                <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
                                <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
                            </>
                        )}
                        <img src={lightboxImages[lightboxIndex]} alt="" className="lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
}
